/**
 * registration-api.ts - API client for tournament registration operations
 *
 * Handles all HTTP requests related to tournament registration and management.
 */

export type RegistrationStatus = 'pending' | 'accepted' | 'rejected' | 'waitlist' | 'withdrawn';
export type PaymentStatus = 'pending' | 'authorized' | 'captured' | 'failed' | 'cancelled';

export interface Registration {
  id: string;
  tournamentId: string;
  userId: string;
  status: RegistrationStatus;
  playerId?: string;
  feePaid: boolean;
  paymentIntentId?: string;
  setupIntentId?: string;
  paymentStatus: PaymentStatus;
  selectedFeeId?: string;
  notes?: string;
  waitlistPosition?: number;
  createdAt: string;
  updatedAt: string;
  // Populated fields
  tournament?: any;
  user?: any;
}

export interface EntryFee {
  id: string;
  tournamentId: string;
  amount: number;
  currency: string;
  description: string;
  isDefault: boolean;
  createdAt: string;
}

/**
 * Register for a tournament
 * @param tournamentId - Tournament ID
 * @param selectedFeeId - Optional selected entry fee ID
 * @param paymentMethod - Payment method: 'online' for Stripe or 'cash' for on-site payment
 * @returns Registration object with payment details if applicable
 * @throws Error if request fails
 */
export async function registerForTournament(
  tournamentId: string,
  selectedFeeId?: string,
  paymentMethod?: 'online' | 'cash'
): Promise<Registration> {
  const response = await fetch(`/api/tournaments/${tournamentId}/register`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ selectedFeeId, paymentMethod })
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to register for tournament');
  }

  return await response.json();
}

/**
 * Withdraw from a tournament
 * @param tournamentId - Tournament ID
 * @throws Error if request fails
 */
export async function withdrawRegistration(tournamentId: string): Promise<void> {
  const response = await fetch(`/api/tournaments/${tournamentId}/register`, {
    method: 'DELETE',
    credentials: 'include'
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to withdraw registration');
  }
}

/**
 * Get user's registrations
 * @returns Array of registration objects with tournament details
 * @throws Error if request fails
 */
export async function getUserRegistrations(): Promise<Registration[]> {
  const response = await fetch('/api/registrations', {
    credentials: 'include'
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to fetch registrations');
  }

  const data = await response.json();
  return data.registrations || [];
}

/**
 * Get tournament registrations (organizer only)
 * @param tournamentId - Tournament ID
 * @returns Object containing pending, accepted, waitlist registrations
 * @throws Error if request fails
 */
export async function getTournamentRegistrations(tournamentId: string): Promise<{
  pending: Registration[];
  accepted: Registration[];
  waitlist: Registration[];
}> {
  const response = await fetch(`/api/tournaments/${tournamentId}/registrations`, {
    credentials: 'include'
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to fetch tournament registrations');
  }

  return await response.json();
}

/**
 * Accept a registration (organizer only)
 * @param tournamentId - Tournament ID
 * @param registrationId - Registration ID
 * @returns Updated registration
 * @throws Error if request fails
 */
export async function acceptRegistration(
  tournamentId: string,
  registrationId: string
): Promise<Registration> {
  const response = await fetch(
    `/api/tournaments/${tournamentId}/registrations/${registrationId}/accept`,
    {
      method: 'POST',
      credentials: 'include'
    }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to accept registration');
  }

  return await response.json();
}

/**
 * Reject a registration (organizer only)
 * @param tournamentId - Tournament ID
 * @param registrationId - Registration ID
 * @returns Updated registration
 * @throws Error if request fails
 */
export async function rejectRegistration(
  tournamentId: string,
  registrationId: string
): Promise<Registration> {
  const response = await fetch(
    `/api/tournaments/${tournamentId}/registrations/${registrationId}/reject`,
    {
      method: 'POST',
      credentials: 'include'
    }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to reject registration');
  }

  return await response.json();
}

/**
 * Move registration to waitlist (organizer only)
 * @param tournamentId - Tournament ID
 * @param registrationId - Registration ID
 * @returns Updated registration
 * @throws Error if request fails
 */
export async function moveToWaitlist(
  tournamentId: string,
  registrationId: string
): Promise<Registration> {
  const response = await fetch(
    `/api/tournaments/${tournamentId}/registrations/${registrationId}/move-to-waitlist`,
    {
      method: 'POST',
      credentials: 'include'
    }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to move to waitlist');
  }

  return await response.json();
}

/**
 * Promote from waitlist (organizer only)
 * @param tournamentId - Tournament ID
 * @param registrationId - Registration ID
 * @returns Updated registration
 * @throws Error if request fails
 */
export async function promoteFromWaitlist(
  tournamentId: string,
  registrationId: string
): Promise<Registration> {
  const response = await fetch(
    `/api/tournaments/${tournamentId}/waitlist/${registrationId}/promote`,
    {
      method: 'POST',
      credentials: 'include'
    }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to promote from waitlist');
  }

  return await response.json();
}

/**
 * Get entry fees for a tournament
 * @param tournamentId - Tournament ID
 * @returns Array of entry fee objects
 * @throws Error if request fails
 */
export async function getEntryFees(tournamentId: string): Promise<EntryFee[]> {
  const response = await fetch(`/api/tournaments/${tournamentId}/entry-fees`, {
    credentials: 'include'
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to fetch entry fees');
  }

  return await response.json();
}

/**
 * Create an entry fee (organizer only)
 * @param tournamentId - Tournament ID
 * @param feeData - Entry fee data
 * @returns Created entry fee
 * @throws Error if request fails
 */
export async function createEntryFee(
  tournamentId: string,
  feeData: Partial<EntryFee>
): Promise<EntryFee> {
  const response = await fetch(`/api/tournaments/${tournamentId}/entry-fees`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(feeData)
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to create entry fee');
  }

  return await response.json();
}

/**
 * Update an entry fee (organizer only)
 * @param tournamentId - Tournament ID
 * @param feeId - Entry fee ID
 * @param feeData - Updated entry fee data
 * @returns Updated entry fee
 * @throws Error if request fails
 */
export async function updateEntryFee(
  tournamentId: string,
  feeId: string,
  feeData: Partial<EntryFee>
): Promise<EntryFee> {
  const response = await fetch(`/api/tournaments/${tournamentId}/entry-fees/${feeId}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(feeData)
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to update entry fee');
  }

  return await response.json();
}

/**
 * Delete an entry fee (organizer only)
 * @param tournamentId - Tournament ID
 * @param feeId - Entry fee ID
 * @throws Error if request fails
 */
export async function deleteEntryFee(tournamentId: string, feeId: string): Promise<void> {
  const response = await fetch(`/api/tournaments/${tournamentId}/entry-fees/${feeId}`, {
    method: 'DELETE',
    credentials: 'include'
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to delete entry fee');
  }
}
